import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import _ from 'lodash';
import Quill, { Delta } from 'quill/core';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useVisitorSession from '../account/useVisitorSession';
import PublicContentView from '../app/layout/PublicContentView';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import PublicFooter from '../components/PublicFooter';
import type { TextChangeHandler } from '../editor/Editor';
import Editor from '../editor/Editor';
import QuillContext, { useQuill } from '../editor/QuillContext';
import WiderLineSizeContext from '../editor/WiderLineSizeContext';
import { EditorActions } from '../editor/editorSlice';
import EditorModal from '../editor/modals/EditorModal';
import NotFoundView from '../error/NotFoundView';
import { gql } from '../graphql/generated/gql';
import { UserRole } from '../topic/topicPolicyCheck';
import UIActions from '../ui/UIActions';
import useSlug from '../utils/router/useSlug';
import { IsPublicViewProvider } from '../utils/useIsPublicView';
import MagneticWall from './MagneticWall';
import PostFooter from './PostFooter';
import { LegacyPostLayoutSizeProvider } from './PostLayoutSizeContext';
import PostSeriesBar from './PostSeriesBar';
import PostSkeleton from './PostSkeleton';
import PostStickyControl from './PostStickyControl';
import { PublicPostTOCProvider, usePostTOC } from './PostTOCContext';
import PostViewBanner from './PostViewBanner';
import styles from './PublicPostView.module.scss';
import PostActionButtons from './actionButtons/PostActionButtons';
import postPolicyCheck, { PostPolicyAction } from './postPolicyCheck';
import PostSeriesFooter from './series/PostSeriesFooter';
import PostTopicListWithBreadcrumbs from './topic/PostTopicListWithBreadcrumbs';

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
gql(`
  fragment PublicPostView_post on PublicPost {
    id
    title
    ...PostContent
    series {
      id
      name
      posts {
        id
        title
      }
    }
    banner {
      original
    }
    topics {
      id
      name
      privacy
      ancestors {
        id
        name
      }
    }
  }
`);

const PUBLIC_ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY = gql(`
  query publicOrganizationAllowedEmbedHosts($host: String!) {
    organization(host: $host) {
      id
      settings {
        allowedEmbedHosts
      }
    }
  }
`);

const PUBLIC_POST_QUERY = gql(`
  query PublicPostView($id: ID!) {
    post: publicPost(id: $id) {
      id
      linkAccess
      ...PublicPostView_post
      ...PublicContentView_post
      ...postPolicyCheck_publicPost
      ...EditorActions_post
      ...Editor_post
    }
  }
`);

const InnerPublicPostView = () => {
  const { isTOCOpen } = usePostTOC();
  const [id] = useSlug('postSlug');

  const { visitorSession, ensureVisitorSession } = useVisitorSession();

  const { quill } = useQuill();
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isLocked = useAppSelector(
    (state) => state.session.organization.isLocked,
  );

  const { data, loading } = useQuery(PUBLIC_POST_QUERY, {
    variables: { id },
  });

  const post = data?.post;

  const isEditable = !readOnly && !!visitorSession;

  const { data: allowedEmbedHostsData } = useQuery(
    PUBLIC_ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY,
    { variables: { host: window.__slab.ORG_HOST } },
  );

  const allowedEmbedHosts = useMemo(
    () =>
      _.compact(
        allowedEmbedHostsData?.organization?.settings?.allowedEmbedHosts,
      ),
    [allowedEmbedHostsData],
  );

  const dispatch = useAppDispatch();

  const postLayoutContainerRef = useRef(null);
  const postBannerContainerRef = useRef<HTMLDivElement | null>(null);

  // Remote updates not applied yet
  const receivedRef = useRef(new Delta());

  const quillRef = useRef(quill);
  useEffect(() => {
    if (!quillRef.current && quill) {
      if (document.location.hash) {
        quill
          .getModule('scrollToAnchor')
          // @ts-expect-error
          ?.scrollToAnchor(document.location.hash);
      }
    }

    quillRef.current = quill;
    return () => {
      quillRef.current = null;
    };
  }, [quill]);

  useEffect(() => {
    const { hash } = document.location;
    if (hash) {
      const header = document.getElementById(hash.slice(1));
      if (header != null) {
        header.scrollIntoView();
      }
    }
  }, [post]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLImageElement;
    if (target.tagName === 'IMG') {
      dispatch(
        UIActions.openModal(EditorModal, { image: { src: target.src } }),
      );
    }
  };

  const isPostEditable =
    !!post &&
    !isLocked &&
    postPolicyCheck(PostPolicyAction.EditPost, post, UserRole.Anonymous);

  const loadedPostIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (post && loadedPostIdRef.current !== post.id) {
      if (!isEditable) {
        dispatch(
          EditorActions.loadInitialContent({
            initialContent: post.content,
            initialVersion: post.version,
          }),
        );
      } else {
        dispatch(
          EditorActions.load({
            post,
            isEditable: isPostEditable,
            onRemoteDelta: (delta: Delta) => {
              receivedRef.current = receivedRef.current.compose(delta);
              quillRef.current?.updateContents(
                receivedRef.current,
                Quill.sources.API,
              );
            },
          }),
        );
        loadedPostIdRef.current = post.id;
      }
    }
  }, [dispatch, isEditable, isPostEditable, post]);

  useEffect(
    () => () => {
      dispatch(EditorActions.leave());
    },
    [dispatch, id],
  );

  useEffect(() => {
    if (!readOnly && isPostEditable && ensureVisitorSession) {
      ensureVisitorSession();
    }
  }, [ensureVisitorSession, isPostEditable, readOnly]);

  const handleTextChange: TextChangeHandler = useCallback(
    (_delta, _oldContent, source) => {
      if (source === Quill.sources.API) {
        receivedRef.current = new Delta();
      }
    },
    [],
  );

  if (!post) {
    return loading ? null : <NotFoundView />;
  }

  const currentBannerUrl = post.banner?.original;
  const hasBanner = currentBannerUrl != null;

  return (
    <LegacyPostLayoutSizeProvider>
      <PublicContentView
        className={classNames(
          'public-post post-container no-padding-x no-padding-y',
          { 'show-sticky-toc': isTOCOpen, 'has-banner': hasBanner },
        )}
        post={post}
      >
        <div
          ref={postLayoutContainerRef}
          className="post-layout-container post-topic-breadcrumb-enabled"
        >
          {post ? (
            <WiderLineSizeContext
              containerRef={postLayoutContainerRef}
              editor={quill ? quill.scroll.domNode : null}
            >
              {currentBannerUrl && (
                <div
                  ref={postBannerContainerRef}
                  className="post-layout-banner"
                >
                  <PostViewBanner banner={currentBannerUrl} />
                </div>
              )}
              <div className="post-layout-above">
                {/* TODO: Fix postTopics type to support public topics when not editing */}
                {/* @ts-expect-error */}
                <PostTopicListWithBreadcrumbs postTopics={post.topics} />
                {post.series && (
                  <PostSeriesBar postId={post.id} series={post.series} />
                )}
              </div>
              <PostStickyControl
                postId={post.id}
                postLayoutBannerRef={postBannerContainerRef}
              />
              <div
                className="post-layout-editor"
                onDoubleClick={handleDoubleClick}
              >
                <div className="editor-container">
                  <Editor
                    allowedEmbedHosts={allowedEmbedHosts}
                    post={post}
                    namespace="publicEditor"
                    onTextChange={handleTextChange}
                    hasBanner={hasBanner}
                  />
                </div>
              </div>
              {isEditable && (
                <aside className="post-layout-comments">
                  {quill && (
                    <MagneticWall
                      quill={quill}
                      post={post}
                      canEditPost={isPostEditable}
                    />
                  )}
                </aside>
              )}
              <div className="post-layout-below">
                {post.series && (
                  <div className={styles.postSeriesBarContainer}>
                    <PostSeriesFooter postId={post.id} series={post.series} />
                    <PostSeriesBar postId={post.id} series={post.series} />
                  </div>
                )}
                <div className={styles.footerContainer}>
                  <PublicFooter />
                </div>
              </div>
            </WiderLineSizeContext>
          ) : (
            <div className="post-layout-editor">
              <div className="post-layout-above" />
              <div className="post-layout-editor-inner">
                <PostSkeleton />
              </div>
            </div>
          )}
        </div>
        <PostFooter>
          <PostActionButtons horizontal postId={id} />
        </PostFooter>
      </PublicContentView>
    </LegacyPostLayoutSizeProvider>
  );
};

const PublicPostView = () => {
  return (
    <IsPublicViewProvider publicView>
      <QuillContext>
        <PublicPostTOCProvider>
          <InnerPublicPostView />
        </PublicPostTOCProvider>
      </QuillContext>
    </IsPublicViewProvider>
  );
};

export default PublicPostView;
